import { useTranslation } from 'react-i18next'
import { Module } from 'types/modules'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { Route, RoutesArray } from 'ui/components/Navigation/types'
import AbSLabsView from 'views/AbSLabsView/AbSLabsView'
import AdminSettingsView from 'views/AdminSettingsView/AdminSettingsView'
import AutoFixUiConfigView from 'views/AutoFixUiConfigView/AutoFixUiConfigView'
import AvailabilityView from 'views/AvailabilityView/AvailabilityView'
import { DayAheadTradingPrototype } from 'views/DayAheadTradingPrototype/DayAheadTradingPrototype'
import { DayAheadTradingRoute } from 'views/DayAheadTradingView/DayAheadTradingView'
import DevView from 'views/DevView/DevView'
import DigitalTwinCollectedSettingsView from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView'
import DigitalTwinOtherSettingsView from 'views/DigitalTwinOtherSettingsView/DigitalTwinOtherSettingsView'
import DigitalTwinSamEnergiView from 'views/DigitalTwinSamEnergiView/DigitalTwinSamEnergiView'
import DigitalTwinBaseSettingsView from 'views/DigitalTwinSettingsView/DigitalTwinBaseSettingsView'
import DigitalTwinDeviationSettingsView from 'views/DigitalTwinSettingsView/DigitalTwinDeviationSettingsView'
import EditTimeSeriesView from 'views/EditTimeSeriesView/EditTimeSeriesView'
import { ElectricityTradingView } from 'views/ElectricityTradingView/ElectricityTradingView'
import EventsView from 'views/EventsView/EventsView'
import ExperimentsView from 'views/ExperimentsView/ExperimentsView'
import FollowupProductionView from 'views/FollowupProductionView/FollowupProductionView'
import FuelPlanView from 'views/FuelPlanView/FuelPlanView'
import ImpersonateUsersView from 'views/ImpersonateUsersView/ImpersonateUsersView'
import { IntradayTradingView } from 'views/IntradayTradingView/IntradayTradingView'
import LogBookView from 'views/LogBookView/LogBookView'
import MFRRTradingView from 'views/MFRRTradingView/MFRRTradingView'
import OptimizationAnalysisView from 'views/OptimizationAnalysisView/OptimizationAnalysisView'
import OptimizationsView from 'views/OptimizationsView/OptimizationsView'
import OptimizeModelSettingsView from 'views/OptimizeModelSettingsView/OptimizeModelSettingsView'
import OptimizeSettingsView from 'views/OptimizeSettingsView/OptimizeSettingsView'
import OptimizeView from 'views/OptimizeView/OptimizeView'
import OptimizeWipView from 'views/OptimizeView/OptimizeWipView'
import OpusUploadView from 'views/OpusUploadView/OpusUploadView'
import ReferenceGuidesView from 'views/ReferenceGuidesView/ReferenceGuidesView'
import ReviewForecastsView from 'views/ReviewForecastsView/ReviewForecastsView'
import SamEnergiView from 'views/SamEnergiView/SamEnergiView'
import SandboxProjectView from 'views/SandboxProjectView/SandboxProjectView'
import SandboxView from 'views/SandboxView/SandboxView'
import SettlementsView from 'views/SettlementsView/SettlementsView'
import ShiftScheduleView from 'views/ShiftScheduleView/ShiftScheduleView'
import StartPageView from 'views/StartpageView/StartpageView'
import UiConfigView from 'views/UiConfigView/UiConfigView'
import UnitAvailabilityView from 'views/UnitAvailabilityView/UnitAvailabilityView'
import UserProfileView from 'views/UserProfileView/UserProfileView'
import DigitalTwinVersionControlView from 'views/VersionControlView/DigitalTwinVersionControlView'

type SystemType = `digitalTwin` | `optModel`

const SETTINGS_PATHS_BY_SYSTEM_TYPE = {
  optimizesettings: { digitalTwin: `/digitalTwinDeviationSettings`, optModel: `/optimizesettings` },
  optimizemodelsettings: { digitalTwin: `/digitalTwinBaseSettings`, optModel: `/optimizemodelsettings` },
} satisfies Partial<Record<Module, Record<SystemType, string>>>

function redirectFromOtherSystemTypePath(
  module: keyof typeof SETTINGS_PATHS_BY_SYSTEM_TYPE,
  systemType: SystemType
): Route {
  const paths = SETTINGS_PATHS_BY_SYSTEM_TYPE[module]
  return {
    path: paths[systemType === `digitalTwin` ? `optModel` : `digitalTwin`],
    module,
    redirect: true,
    to: paths[systemType],
  }
}

export function useMainRoutes(): RoutesArray {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  const isDigitalTwin = !!activeSystem?.primary_digital_twin
  const systemType: SystemType = isDigitalTwin ? `digitalTwin` : `optModel`

  return [
    {
      path: `/startpage`,
      sidebarName: t(`Startpage`),
      navbarName: t(`Startpage`),
      module: `startpage`,
      icon: `fal fa-home`,
      component: StartPageView,
    },
    {
      path: `/optimizationanalysis`,
      notInSidemenu: true,
      module: `optimizationanalysis`,
      component: OptimizationAnalysisView,
    },
    {
      path: `/optimize`,
      sidebarName: t(`Production plan`),
      navbarName: t(`Production plan`),
      module: `optimize`,
      icon: `fal fa-chart-area`,
      component: OptimizeView,
    },
    {
      path: `/draftproductionplan`,
      sidebarName: t(`Draft production plan`),
      navbarName: t(`Draft production plan`),
      module: `optimizewip`,
      icon: `fal fa-project-diagram`,
      component: OptimizeWipView,
    },
    {
      sidebarName: t(`Settings`),
      icon: `fal fa-cogs`,
      module: `settings`,
      children: [
        {
          path: SETTINGS_PATHS_BY_SYSTEM_TYPE.optimizesettings[systemType],
          sidebarName: t(`Deviation settings`),
          navbarName: t(`Deviation settings`),
          module: `optimizesettings`,
          icon: `fal fa-wrench`,
          component: isDigitalTwin ? DigitalTwinDeviationSettingsView : OptimizeSettingsView,
        },
        {
          path: SETTINGS_PATHS_BY_SYSTEM_TYPE.optimizemodelsettings[systemType],
          sidebarName: isDigitalTwin ? t('Base settings') : t(`Opt model settings`),
          navbarName: isDigitalTwin ? t('Base settings') : t(`Opt model settings`),
          module: `optimizemodelsettings`,
          icon: `fal fa-cog`,
          component: isDigitalTwin ? DigitalTwinBaseSettingsView : OptimizeModelSettingsView,
        },
        ...(isDigitalTwin
          ? [
              {
                path: '/digitalTwinCollectedSettings',
                sidebarName: t('Collected settings'),
                navbarName: t('Collected settings'),
                module: `collectedsettings` as Module,
                icon: `fal fa-cogs`,
                component: DigitalTwinCollectedSettingsView,
              },
              {
                path: '/digitalTwinOtherSettings',
                sidebarName: t('Other settings'),
                navbarName: t('Other settings'),
                module: `othersettings` as Module,
                icon: `fal fa-mask`,
                component: DigitalTwinOtherSettingsView,
              },
            ]
          : []),
      ],
    },
    redirectFromOtherSystemTypePath(`optimizesettings`, systemType),
    redirectFromOtherSystemTypePath(`optimizemodelsettings`, systemType),
    {
      sidebarName: t(`Follow-up`),
      icon: `fal fa-history`,
      module: `followup`,
      children: [
        {
          path: `/followupproduction`,
          sidebarName: t(`Follow-up: Production`),
          sidebarNameAsChild: t(`Production`),
          navbarName: t(`Follow-up: Production`),
          module: `followupproduction`,
          icon: `fal fa-chart-bar`,
          component: FollowupProductionView,
        },
        {
          sidebarName: t(`Review forecasts`),
          sidebarNameAsChild: t(`Review forecasts`),
          navbarName: t(`Review forecasts`),
          path: `/reviewforecasts`,
          module: `reviewforecasts`,
          icon: `fal fa-chart-line`,
          component: ReviewForecastsView,
        },
      ],
    },
    {
      path: `/events/:eventId`,
      sidebarName: t('Events'),
      navbarName: t('Events'),
      module: `events`,
      icon: `fal fa-calendar-week`,
      notInSidemenu: true,
      alwaysAvailable: true,
      component: EventsView,
    },
    {
      path: `/events/:eventId/edit`,
      sidebarName: t('Events'),
      navbarName: t('Events'),
      module: `events`,
      icon: `fal fa-calendar-week`,
      notInSidemenu: true,
      alwaysAvailable: true,
      component: EventsView,
    },
    {
      path: `/events`,
      sidebarName: t('Events'),
      navbarName: t('Events'),
      module: `events`,
      icon: `fal fa-calendar-week`,
      component: EventsView,
    },
    {
      path: `/edittimeseries`,
      sidebarName: t(`Data Correction`),
      navbarName: t(`Data Correction`),
      module: `edittimeseries`,
      icon: `fal fa-table-cells-header`,
      component: EditTimeSeriesView,
    },
    {
      path: `/optimizations`,
      sidebarName: t(`Review optimizations`),
      navbarName: t(`Review optimizations`),
      module: `optimizations`,
      icon: `fal fa-tasks`,
      component: OptimizationsView,
    },
    {
      path: `/logbook`,
      sidebarName: t(`Logbook`),
      navbarName: t(`Logbook`),
      module: `logbook`,
      icon: `fal fa-sticky-note`,
      component: LogBookView,
    },
    {
      path: `/shiftschedule`,
      sidebarName: t(`Shift Schedule`),
      navbarName: t(`Shift Schedule`),
      module: `shiftschedule`,
      icon: `fal fa-user-clock`,
      component: ShiftScheduleView,
    },
    {
      path: `/dev`,
      module: `devView`,
      component: DevView,
      notInSidemenu: true,
      alwaysAvailable: true,
    },
    {
      path: `/labs`,
      module: `abSLabsView`,
      component: AbSLabsView,
      notInSidemenu: true,
      alwaysAvailable: true,
    },
    {
      path: `/samenergi`,
      sidebarName: t(`SamEnergi`),
      navbarName: t(`SamEnergi`),
      module: `samenergi`,
      icon: `fal fa-handshake`,
      component: activeSystem?.primary_digital_twin ? DigitalTwinSamEnergiView : SamEnergiView,
    },
    {
      path: `/availability`,
      sidebarName: t(`Availability`),
      navbarName: t(`Availability`),
      module: `availability`,
      icon: `fal fa-percentage`,
      component: activeSystem?.primary_opt_model ? AvailabilityView : UnitAvailabilityView,
    },
    {
      path: `/sandbox/:id/`,
      navbarName: t(`Sandbox`),
      module: `sandbox`,
      notInSidemenu: true,
      alwaysAvailable: true,
      component: SandboxProjectView,
    },
    {
      path: `/sandbox`,
      sidebarName: t(`Sandbox`),
      navbarName: t(`Sandbox`),
      module: `sandbox`,
      icon: `fal fa-flask`,
      component: SandboxView,
    },
    {
      path: `/settlements`,
      sidebarName: t(`Settlements`),
      navbarName: t(`Settlements`),
      module: `settlements`,
      icon: `far fa-calculator-alt`,
      component: SettlementsView,
    },
    {
      path: `/referenceguides`,
      sidebarName: t(`Reference guides`),
      navbarName: t(`Reference guides`),
      module: `referenceguides`,
      icon: `fal fa-book`,
      component: ReferenceGuidesView,
    },
    {
      component: DigitalTwinVersionControlView,
      icon: `fal fa-user-friends`,
      module: `digitaltwinversioncontrol`,
      navbarName: t(`Version Control Digital Twin`),
      path: `/digitalTwinVersionControl`,
      sidebarName: t(`Version Control Digital Twin`),
    },
    {
      path: `/electricitytrading`,
      sidebarName: t(`Electricity Trading`),
      navbarName: t(`Electricity Trading`),
      module: `electricitytrading`,
      icon: `far fa-bolt`,
      component: ElectricityTradingView,
    },
    {
      path: `/dayaheadtrading`,
      sidebarName: t(`Day Ahead Trading`),
      navbarName: t(`Day Ahead Trading`),
      module: `dayaheadtrading`,
      icon: `fal fa-calendar-day`,
      component: DayAheadTradingRoute,
    },
    {
      path: `/dayaheadtradingprototype`,
      sidebarName: t(`Day Ahead Trading (Prototype)`),
      navbarName: t(`Day Ahead Trading (Prototype)`),
      module: `dayaheadtradingprototype`,
      icon: `fal fa-vial`,
      component: DayAheadTradingPrototype,
    },
    {
      path: `/mfrrtrading`,
      sidebarName: t(`mFRR`),
      navbarName: t(`mFRR`),
      module: `mfrrtrading`,
      icon: `fal fa-plug-circle-bolt`,
      component: MFRRTradingView,
    },
    {
      path: `/intradaytrading`,
      sidebarName: t(`Intraday Trading`),
      navbarName: t(`Intraday Trading`),
      module: `intradaytrading`,
      icon: `fal fa-business-time`,
      component: IntradayTradingView,
    },
    {
      path: `/fuelplan`,
      module: `fuelplan`,
      sidebarName: t('Scenario planning'),
      navbarName: t('Scenario planning'),
      component: FuelPlanView,
      icon: `fal fa-layer-group`,
    },
    {
      path: `/opusupload`,
      module: `opusupload`,
      sidebarName: t('OPUS Upload'),
      navbarName: t('OPUS Upload'),
      component: OpusUploadView,
      icon: `fal fa-upload`,
    },
    {
      path: `/userprofile`,
      module: `userpage`,
      component: UserProfileView,
      notInSidemenu: true,
      alwaysAvailable: true,
    },
    {
      path: `/adminsettings`,
      module: `useradmin`,
      component: AdminSettingsView,
      globalModule: true,
      notInSidemenu: true,
    },
    {
      path: `/impersonate`,
      module: `impersonate`,
      component: ImpersonateUsersView,
      globalModule: true,
      notInSidemenu: true,
    },
    {
      path: `/uiconfig/:uid?`,
      module: `uiconfig`,
      component: UiConfigView,
      notInSidemenu: true,
    },
    {
      path: `/autofixuiconfig`,
      module: `uiconfig`,
      component: AutoFixUiConfigView,
      notInSidemenu: true,
    },
    {
      path: `/experiments`,
      module: `experiments`,
      component: ExperimentsView,
      notInSidemenu: true,
      alwaysAvailable: true,
    },
    {
      module: `startpage`,
      redirect: true,
      path: `/`,
      to: `/startpage`,
    },
  ]
}
